<template>
  <div class="admin-physician-schedule">
    <h5 class="heading">Physician Schedule</h5>
    <p class="heading-content">Add physicians to enable them to receive invitations for tumor board sessions. If a
      physician regularly attends a specific tumor board, assign them accordingly.<br> Otherwise, keep the "Assign Tumor
      Board" column empty and use the “Invite Physician” page.</p>
    <div class="search-add-icons">
      <span class="p-input-icon-right">
        <InputText v-model="searchVal" placeholder="Filter Physicians" @keyup="requestSearch" size="small" />
        <span v-if="!searchVal" class="material-symbols-outlined size-20 icon-find-a-physician">filter_alt</span>
        <span v-else class="material-symbols-outlined size-20 icon-find-a-physician icon-clear-filter"
          @click="() => { searchVal = '', requestSearch() }">close</span>
      </span>
      <button class="search-physician primary-filled-button" @click="onOpenFindPhysicianModal">
        <i class="material-symbols-outlined">add</i> <span>Add Physician</span>
      </button>
    </div>
    <div class="scheduled-physicians-div">
      <DataTable v-if="!physicianSpecialityList.length && !isLoading">
        <template #empty>
          <p class="no-records-class"> No Specialties available</p>
        </template>
        <Column field="caregiver_name" header="Name" sortable></Column>
        <Column field="caregiver_email" header="Email Id"></Column>
        <Column field="dept_name" header="Specialty"></Column>
      </DataTable>
      <DataTable v-else-if="!isLoading" :value="filteredPhysicianData || getAdminPhysicianScheduleResponse" paginator
        editMode="cell" tableStyle="min-width: 10rem" :alwaysShowPaginator=false tableClass="editable-cells-table" :rows=5
        @cell-edit-complete="onCellEditCompleteAdminPhysicianSchedule" @row-click="onRowClick">
        <Column field="caregiver_name" header="Name" sortable></Column>
        <Column field="caregiver_email" header="Email Id"></Column>
        <Column header="Specialty">
          <template #body="rowData">
            <MultiSelectDropdown department="Specialty" :placeholder="specialtyPlaceholder"
              :ref="(el) => setMultiSelectRef(`cgsp-${rowData.data.caregiver_id}`, el)"
              :defaultOptions="calculateDefaultSpecialtyOptions(rowData.data)"
              :options="calculateSpecialtyOptionsValues(rowData.data)" :limit="1"
              @selected-values-changed="handleSpecialtyChange" />
          </template>
        </Column>
        <Column header="Assign Tumor Board">
          <template #body="rowData">
            <MultiSelectDropdown department="Tumor Board" :placeholder="tbPlaceholder"
              :ref="(el) => setMultiSelectRef(`cgtb-${rowData.data.caregiver_id}`, el)"
              :defaultOptions="calculateDefaultOptions(rowData.data.tb_presence)" :options="calculateOptionsValues()"
              @selected-values-changed="onSelectedValuesChanged" />
          </template>
        </Column>
        <Column>
          <template #body="rowData">
            <span :class="[
              'material-symbols-outlined',
              'icon-button-to-primary',
              'save-btn',
              { disabled: !savablePhysicians.map(p => p.id).includes(rowData.data.caregiver_id) }]"
              @click="handleSave(rowData.data)">save</span>
            <span class="material-symbols-outlined icon-button-to-primary size-20 icon-delete" title="Delete Row"
              @click="handleDelete(rowData)">cancel</span>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
  <find-physician-modal :open="findPhysicianModalOpen" @close="onCloseFindPhysicianModal">
  </find-physician-modal>
  <Dialog v-model:visible="deletePopUpvisible" modal header="Delete Physician">
    <p>Are you sure you want to delete physician?</p>
    <div class="flex gap-2">
      <button class="primary-outlined-button cancel-btn" @click.prevent="handleModalCancel">Cancel</button>
      <button class="primary-filled-button" @click.prevent="deleteOnConfirm">Confirm</button>
    </div>
  </Dialog>
</template>

<script setup>
// Copyright (c) 2020-2023 Omics Data Automation, Inc.
// Copyright (c) 2023 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { computed, defineProps, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import FindPhysicianModal from '@/ux3/components/FindPhysicianModal/FindPhysicianModal.vue'
import { getAdminPhysicianSchedule } from './getAdminPhysicianSchedule.js'
import { getPhysicianSpeciality } from './getPhysicianSpeciality.js'
import { patchPhysicianAdminData } from './patchPhysicianAdminData.js'
import { postPhysicianAdminData } from './postPhysicianAdminData.js'
import { getTumorBoardTypes } from './getTumorBoardTypes.js'
import MultiSelectDropdown from '@/ux3/components/TBPhysicianSchedule/MultiSelectDropdown.vue'
import InputText from 'primevue/inputtext'
import Dialog from 'primevue/dialog'
import { showToast } from '../../../utils/utils'
import { updateCalendarInvite } from '@/ux3/apis/updateCalendarInvite.js'

// This prop is not being used, but we need to handle it to prevent warnings
// since the parent component is dynamically rendering children that expect this prop.
defineProps({
  componentProps: {
    type: Object,
    required: true,
  },
})

defineEmits(['form-change', 'reset-confirmation'])

const store = useStore()
const getAdminPhysicianScheduleResponse = ref([])
const physicianSpecialityList = ref([])
const patchPhysicianAdminDataResponse = ref('')
const selectedRow = ref([])
const searchVal = ref('')
const filteredPhysicianData = ref('')
const deletePopUpvisible = ref(false)
const selectedRowToDelete = ref('')
const getTumorBoardTypesResponse = ref([])
const isDeleteBtnEnabled = ref(true)
const isLoading = ref(true)
const multiSelectRefs = ref({})
const savablePhysicians = ref([])

const findPhysicianModalOpen = computed(() => store.getters.isFindPhysicianModalOpen || false)
const selectedPhysician = computed(() => store.state.session.physician)

const specialtyPlaceholder = 'Select a specialty'
const tbPlaceholder = 'Select tumorboards'

onMounted(async () => {
  getTumorBoardTypesResponse.value = await getTumorBoardTypes()
  getAdminPhysicianScheduleResponse.value = await getAdminPhysicianSchedule()
  physicianSpecialityList.value = await getPhysicianSpeciality()
  isLoading.value = false
})

watch(selectedPhysician, () => {
  if (getAdminPhysicianScheduleResponse.value.some(physician => physician.caregiver_email === selectedPhysician.value.mail)) {
    showToast('error', `The email address ${selectedPhysician.value.mail} is already in the list`)
    return
  }
  if (!selectedPhysician.value.mail) {
    showToast('error', `The email address cannot be empty`)
    return
  }
  getAdminPhysicianScheduleResponse.value.unshift({
    caregiver_id: selectedPhysician.value.id,
    caregiver_email: selectedPhysician.value.mail,
    caregiver_name: selectedPhysician.value.displayName,
    caregiver_title: selectedPhysician.value.jobTitle,
    dept_id: '',
    dept_name: '',
    tb_presence: [],
    isNew: true,
  })
  requestSearch()
  isDeleteBtnEnabled.value = false
})

const bulkUpdateTumorboardIds = async (tbPresence) => {
  const promises = tbPresence.map((tb) => updateCalendarInvite(tb.tb_id))
  await Promise.all(promises)
}

const setMultiSelectRef = (key, el) => {
  if (el) { multiSelectRefs.value[key] = el }
}

const filterSaveablePhysicians = (originalArr, incomingId) => originalArr.filter(physician => physician.id !== incomingId)

const doesItemMatchSearchValue = (item, searchValue) => {
  const lowerCaseSearchValue = searchValue.toLowerCase()
  const propertiesToSearch = [
    item.caregiver_name,
    item.caregiver_email,
    item.caregiver_title,
    item.dept_name,
    ...item.tb_presence.map(tb => tb.tb_name),
  ]
  return propertiesToSearch.some(property => property?.toLowerCase().includes(lowerCaseSearchValue))
}

const requestSearch = () => {
  const filteredData = getAdminPhysicianScheduleResponse.value.filter(item => doesItemMatchSearchValue(item, searchVal.value))
  filteredPhysicianData.value = filteredData
}

const handleDelete = (rowData) => {
  deletePopUpvisible.value = true
  selectedRowToDelete.value = rowData
}

const deleteOnConfirm = async () => {
  const rowData = selectedRowToDelete.value
  if (rowData.data.isNew) {
    getAdminPhysicianScheduleResponse.value = getAdminPhysicianScheduleResponse.value.filter((item) => {
      return item.caregiver_name !== rowData.data.caregiver_name
    })
    showToast('success', 'Deleted Physician')
    deletePopUpvisible.value = false
    selectedRowToDelete.value = ''
    return
  }
  const caregiver_id = rowData?.data?.caregiver_id
  const data = { 'is_active': false }
  patchPhysicianAdminDataResponse.value = await patchPhysicianAdminData(caregiver_id, data)
  if (patchPhysicianAdminDataResponse.value === caregiver_id) {
    showToast('success', 'Deleted Physician')
    selectedRowToDelete.value = ''
    deletePopUpvisible.value = false
    getAdminPhysicianScheduleResponse.value = await getAdminPhysicianSchedule()
  }
  else {
    showToast('error', 'Unable to delete Physician')
  }
}

const addChangeToSaveablePhysicians = (id, diff) => {
  const index = savablePhysicians.value.findIndex(caregiver => caregiver.id === id)

  // Add from tb dropdown
  if (diff.length && 'tb_id' in diff[0]) {
    index !== -1
      ? savablePhysicians.value[index].tb_presence = diff
      : savablePhysicians.value.push({ id: id, tb_presence: diff })
  }
  // Add from specialty dropdown
  if ('dept_id' in diff) {
    index !== -1
      ? savablePhysicians.value[index] = { ...savablePhysicians.value[index], ...diff }
      : savablePhysicians.value.push({ id: id, ...diff })
  }
}

const onSelectedValuesChanged = () => {
  const originalSelection = selectedRow.value.tb_presence
  const tbDropdownValue = multiSelectRefs.value[`cgtb-${selectedRow.value.caregiver_id}`]
  const equal = arraysAreEqual(originalSelection, tbDropdownValue.selectedValues)
  const id = selectedRow.value.caregiver_id
  if (!equal) {
    const diff = getTbDiff(originalSelection, tbDropdownValue.selectedValues)
    addChangeToSaveablePhysicians(id, diff)
  }
  else {
    savablePhysicians.value = filterSaveablePhysicians(savablePhysicians.value, id)
  }
}

const handleSpecialtyChange = () => {
  // spDropdownValue.selectedValues is stored as an array similar to tumorboards,
  // but this instance is only allowing 1 selection, so we can safely assume the array
  // will not have more than 1 member.
  const spDropdownValueArr = multiSelectRefs.value[`cgsp-${selectedRow.value.caregiver_id}`].selectedValues
  const deptValEqual = selectedRow.value?.dept_id === spDropdownValueArr[0]?.deptId
  const id = selectedRow.value.caregiver_id

  if (!deptValEqual) {
    const dropdownValue = spDropdownValueArr[0] || {}
    const { deptId: dept_id = undefined, label: dept_name = undefined } = dropdownValue
    const diff = { dept_id, dept_name }
    addChangeToSaveablePhysicians(id, diff)
  }
  else {
    savablePhysicians.value = filterSaveablePhysicians(savablePhysicians.value, id)
  }
}

const onCellEditCompleteAdminPhysicianSchedule = (event) => {
  const { data, newValue, field, value } = event
  if (value === newValue) { return }
  switch (field) {
    case 'dept_name':
      data[field] = typeof newValue === 'string' ? newValue : newValue[field]
      break
    case 'tb_presence':
      data[field] = typeof newValue === 'string' ? newValue : newValue[field]
      break
    default:
      data[field] = newValue
      break
  }
}

const handleSave = async (rowData) => {
  const physicianObj = savablePhysicians.value.find(phys => phys.id === rowData.caregiver_id)
  const changedTbPresenceListFormatted = physicianObj?.tb_presence?.map(tb => ({ tb_id: tb.tb_id, is_active: tb.is_active }))
  const physicianSpecialty = physicianObj?.dept_id
  const caregiver_id = rowData.caregiver_id
  let caregiverUpdatedDetails = {}

  if (physicianSpecialty && !changedTbPresenceListFormatted) {
    caregiverUpdatedDetails = {
      dept_id: physicianSpecialty,
    }
  }

  if (changedTbPresenceListFormatted?.length > 0 && !physicianSpecialty) {
    caregiverUpdatedDetails = {
      tb_presence: changedTbPresenceListFormatted,
    }
  }

  if (physicianSpecialty && changedTbPresenceListFormatted?.length > 0) {
    caregiverUpdatedDetails = {
      dept_id: physicianSpecialty,
      tb_presence: changedTbPresenceListFormatted,
    }
  }

  console.log('selectedPhysician.value', selectedPhysician.value)
  console.log('caregiver_id', caregiver_id)
  if (selectedPhysician.value?.id === caregiver_id) {
    const physician_details = {
      caregiver_title: selectedPhysician.value.jobTitle || '',
      caregiver_name: selectedPhysician.value.displayName,
      caregiver_email: selectedPhysician.value.mail,
      dept_id: physicianSpecialty,
      tb_presence: changedTbPresenceListFormatted,
    }

    const postPhysicianAdminDataResponse = await postPhysicianAdminData(physician_details)
    if (postPhysicianAdminDataResponse.detail) {
      showToast('error', postPhysicianAdminDataResponse.detail)
    } else if (postPhysicianAdminDataResponse) {
      showToast('success', 'Changes Saved')
      savablePhysicians.value = filterSaveablePhysicians(savablePhysicians.value, caregiver_id)
      isDeleteBtnEnabled.value = true
      getAdminPhysicianScheduleResponse.value = await getAdminPhysicianSchedule()
    }
    else {
      showToast('error', 'Changes not Saved')
    }
  } else {
    patchPhysicianAdminDataResponse.value = await patchPhysicianAdminData(caregiver_id, caregiverUpdatedDetails)
    if (patchPhysicianAdminDataResponse.value === caregiver_id) {
      showToast('success', 'Changes Saved')
      savablePhysicians.value = filterSaveablePhysicians(savablePhysicians.value, caregiver_id)
      replaceWithNewPhysicianChanges(caregiver_id, physicianObj)
      await bulkUpdateTumorboardIds(caregiverUpdatedDetails.tb_presence)
    }
    else {
      showToast('error', 'Changes not saved')
    }
  }
}

const replaceWithNewPhysicianChanges = (id, updateObj) => {
  const index = getAdminPhysicianScheduleResponse.value.findIndex(caregiver => caregiver.caregiver_id === id)
  if (index === -1) {
    console.error('Physician was not found in getAdminPhysicianScheduleResponse. The UI has not been updated correctly.')
    return
  }

  if ('tb_presence' in updateObj) {
    const updatedTbPresence = [...getAdminPhysicianScheduleResponse.value[index].tb_presence]

    updateObj.tb_presence.forEach(updatedTb => {
      if (!updatedTb.is_active) {
        // Remove tumorboard
        const tbIndex = updatedTbPresence.findIndex(tb => tb.tb_id === updatedTb.tb_id)
        updatedTbPresence.splice(tbIndex, 1)
      } else {
        // Add tumorboard
        const tbExists = updatedTbPresence.find(tb => tb.tb_id === updatedTb.tb_id)
        if (!tbExists) updatedTbPresence.push(updatedTb)
      }
    })

    getAdminPhysicianScheduleResponse.value[index] = {
      ...getAdminPhysicianScheduleResponse.value[index],
      tb_presence: updatedTbPresence,
    }
  }

  // Replace specialty properties with updated values
  if ('dept_id' in updateObj && 'dept_name' in updateObj) {
    getAdminPhysicianScheduleResponse.value[index] = {
      ...getAdminPhysicianScheduleResponse.value[index],
      dept_id: updateObj.dept_id,
      dept_name: updateObj.dept_name,
    }
  }
}

const calculateDefaultOptions = (tb_presence) => {
  const defaultOptions = tb_presence
    .filter((tb) => tb.is_active)
    .map((tb) => ({
      label: tb.tb_name,
      tbId: tb.tb_id,
    }))
  return defaultOptions
}

const calculateOptionsValues = () => {
  const options = getTumorBoardTypesResponse.value?.tb_types
    ?.map((tb) => ({
      label: tb.tb_name,
      tbId: tb.tb_id,
    }))
  return options
}
const calculateDefaultSpecialtyOptions = (data) => {
  if (data?.dept_name) { return [{ label: data.dept_name, deptId: data?.dept_id }] }
  return []
}

const calculateSpecialtyOptionsValues = (data) => {
  const currentSpecialty = { label: data.dept_name, deptId: data?.dept_id }
  let options = physicianSpecialityList?.value?.map((item) => ({
    label: item.name,
    deptId: item?.dept_id,
  }))

  options.push(currentSpecialty)

  // Remove empty option
  options = options.filter(item => item.label && item?.deptId)
  // Deduplicate
  const optionsDeduplicated = options.filter((item, index, array) =>
    array.findIndex(t => t.label === item.label && t?.deptId === item?.deptId) === index,
  )

  return optionsDeduplicated
}

const arraysAreEqual = (originalArr, selectedValuesArr) => {
  if (originalArr.length !== selectedValuesArr.length) {
    return false
  }

  // Create sets from the arrays to remove duplicates if they somehow exist
  const set1 = new Set(originalArr.map(obj => `${obj.tb_name || ''}`))
  const set2 = new Set(selectedValuesArr.map(obj => `${obj.label || ''}`))

  // Compare the sets for equality
  return set1.size === set2.size && [...set1].every(value => set2.has(value))
}

const getTbDiff = (originalArr, selectedValuesArr) => {
  const names = originalArr.map(obj => obj.tb_name)
  const labels = selectedValuesArr.map(obj => obj.label)

  const extraObjects = selectedValuesArr.filter(obj => !names.includes(obj.label))
    .map(obj => {
      const { label, tbId, ...rest } = obj
      return {
        ...rest,
        tb_id: tbId,
        tb_name: label,

        is_active: true,
      }
    })

  const missingObjects = originalArr.filter(obj => !labels.includes(obj.tb_name))
    .map(obj => ({ ...obj, is_active: false }))

  return [...extraObjects, ...missingObjects]
}

const onRowClick = (event) => selectedRow.value = event.data

const handleModalCancel = () => deletePopUpvisible.value = false

const onOpenFindPhysicianModal = () => {
  store.commit('setFindPhysicianModalOpen', true)
}

const onCloseFindPhysicianModal = () => {
  store.commit('setFindPhysicianModalOpen', false)
}
</script>

<style>
.save-btn {
  color: var(--Primary);
}

.disabled.save-btn {
  fill: var(--Grey500);
  color: var(--Grey500);
  cursor: none;
  pointer-events: none;
}

.admin-physician-schedule {
  margin-left: 2rem;
}

.icon-find-a-physician {
  position: absolute;
  top: 10px;
  right: 12px;
}

.search-physician {
  margin-left: 1rem;
  top: 5px;
  display: flex;
  align-items: center;
}

.icon-delete {
  position: relative;
  top: -1px;
  left: 10px;
}

.icon-delete:hover {
  color: var(--ErrorRedDark);
}

.icon-clear-filter {
  cursor: pointer;
}

.icon-clear-filter:hover {
  color: var(--ErrorRedDark);
}


.search-add-icons {
  margin: 2rem 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.heading {
  font-size: 1rem;
  font-style: normal;
  font-weight: 700;
  margin-left: 0;
}

.heading-content {
  color: var(--Grey600);
  font-size: 0.9rem;
  font-style: normal;
  font-weight: 300;
}

button i {
  margin-right: 8px;
}

.scheduled-physicians-div .p-datatable .p-sortable-column:first-child .p-sortable-column-icon {
  margin-left: 0;
}

.dropdown-open {
  margin-bottom: 0;
  appearance: none;
  width: 100%;
  margin-bottom: none;
  cursor: pointer;
  padding: 0.75rem 0.75rem;
  min-width: 160px;
  border-color: var(--Grey300);
  font-size: 12px;
}

tr {
  position: relative;
}

.p-cell-editing:has(select.edit-disabled):after,
td:has(select.edit-disabled):after {
  content: none;
}

/* TODO: Move shared styles to another stylesheet and scope this style block */
.p-cell-editing select,
td select {
  appearance: none;

}

.p-editable-column {
  cursor: pointer;
  transition: all linear 200ms;
  position: relative;
}

.p-editable-column:hover {
  cursor: pointer;
  color: var(--Primary) !important;
  font-weight: 900 !important;
}

.scheduled-physicians-div {
  .p-datatable-responsive-scroll {
    margin: 0;
  }
}
</style>

<style scoped>
td:last-child {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 73px;
}

.heading {
  margin-left: 0;
  margin-right: 0;
}
</style>
